import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, CircleDot, Lock } from 'lucide-react';
import { useCatalog, useMyProgress } from '../lib/data';
import type { Progress, Track } from '../types';

type State = 'done' | 'now' | 'next' | 'locked';
interface Stage {
  title: string;
  text: string;
  state: State;
  href?: string;
  detail?: string;
  optional?: boolean;
}

const FOUNDATIONS = 'track_cert_foundations';
const CONFIGURATOR = 'track_cert_configurator';
const ARCHITECTURE = 'track_cert_architecture';
const IRM = 'track_cert_irm';
const WELCOME = 'track_welcome';
const ROLE = 'track_aspirant_role';
const VIVA_STEP = 'step_cfg_viva';
const APPLY_STEP = 'step_ar_apply';

function trackState(track: Track | undefined, p: Progress | null): { state: State; detail?: string } {
  if (!track) return { state: 'locked' };
  if (p?.status === 'completed') return { state: 'done', detail: 'Certified' };
  const done = p ? Object.values(p.steps ?? {}).filter((s) => s.done).length : 0;
  const total = track.stepCount || p?.totalSteps || 0;
  return done > 0 ? { state: 'now', detail: `${done} of ${total} steps` } : { state: 'next', detail: total ? `${total} steps` : undefined };
}

/**
 * The whole path on one card. Candidates see the hiring journey, from shortlist interview to offer;
 * teammates see the three certifications. Stages read live progress; a course a Khalifa has not
 * opened for this person shows as locked.
 */
export default function YourPath({ uid, candidate }: { uid: string; candidate: boolean }) {
  const { tracks } = useCatalog();
  const byId = new Map(tracks.map((t) => [t.id, t]));
  const welcome = useMyProgress(uid, candidate ? WELCOME : undefined).progress;
  const role = useMyProgress(uid, candidate ? ROLE : undefined).progress;
  const fnd = useMyProgress(uid, FOUNDATIONS).progress;
  const cfg = useMyProgress(uid, CONFIGURATOR).progress;
  // Architecture sits between Configurator and IRM, and shows once its track is published.
  const hasArchitecture = byId.has(ARCHITECTURE);
  const arch = useMyProgress(uid, hasArchitecture ? ARCHITECTURE : undefined).progress;
  const irm = useMyProgress(uid, IRM).progress;

  const f = trackState(byId.get(FOUNDATIONS), fnd);
  const c = trackState(byId.get(CONFIGURATOR), cfg);
  const a = trackState(byId.get(ARCHITECTURE), arch);
  const i = trackState(byId.get(IRM), irm);
  const vivaDone = cfg?.steps?.[VIVA_STEP]?.done === true;
  const link = (id: string) => (byId.has(id) ? `/track/${id}` : undefined);

  const stages: Stage[] = [];
  if (candidate) {
    stages.push({
      title: 'Get to know Henoyo',
      text: 'Two short welcome films, then an honest look at the role.',
      state: role?.status === 'completed' ? 'done' : 'now',
      href: link(welcome?.status === 'completed' ? ROLE : WELCOME),
    });
    stages.push({
      title: 'Shortlist interview',
      text: 'A conversation with the team. If it goes well, we open Henoyo Foundations for you.',
      state: byId.has(FOUNDATIONS) ? 'done' : 'next',
      href: byId.has(ROLE) ? `/track/${ROLE}/step/${APPLY_STEP}` : undefined,
      ...(byId.has(FOUNDATIONS) ? {} : { detail: 'See how to apply' }),
    });
  }
  stages.push({
    title: 'Henoyo Foundations',
    text: 'Security in plain words, and how to turn a customer\'s spreadsheets into a data model. Pass the exam at 12 of 20.',
    ...f,
    href: link(FOUNDATIONS),
    ...(f.state === 'locked' && candidate ? { detail: 'Opens after your shortlist interview' } : {}),
  });
  stages.push({
    title: 'Henoyo Configurator, on your own Mitti',
    text: 'Build in Henoyo, then a capstone on your Mitti that a checker grades. A Khalifa gives you a Mitti when this opens.',
    ...c,
    href: link(CONFIGURATOR),
    ...(c.state === 'locked' ? { detail: 'Opens after you pass Foundations' } : {}),
  });
  if (hasArchitecture) {
    stages.push({
      title: 'Henoyo Architecture',
      text: 'How Henoyo is built and why: Core, the base objects, the security spine and solution packs. Pass the exam at 21 of 30.',
      ...a,
      href: link(ARCHITECTURE),
      ...(a.state === 'locked' ? { detail: 'Opens after you pass Configurator' } : {}),
    });
  }
  stages.push({
    title: 'IRM Practitioner',
    text: 'Integrated risk, with an assignment on your Mitti.',
    ...i,
    href: link(IRM),
    optional: candidate,
    ...(i.state === 'locked' ? { detail: candidate ? 'Optional; a Khalifa may open it' : 'Opens after Configurator' } : {}),
  });
  stages.push({
    title: 'Demo and viva',
    text: 'A 30 to 45 minute live demo of what you built, then questions from the team. A Khalifa signs it off.',
    state: vivaDone ? 'done' : cfg?.steps?.step_cfg_exam?.done ? 'now' : 'locked',
    detail: vivaDone ? 'Signed off' : cfg?.steps?.step_cfg_exam?.done ? 'Your Khalifa will book it with you' : 'After the Configurator exam',
  });
  if (candidate) {
    stages.push({
      title: 'Offer',
      text: 'If the demo goes well, we talk about joining the team.',
      state: vivaDone ? 'next' : 'locked',
    });
  }

  return (
    <section className="mt-4 rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface">
      <p className="text-xs font-semibold uppercase tracking-wider text-terracotta-500">Your path</p>
      <p className="mt-0.5 text-sm text-ink-500 dark:text-paper-300">
        {candidate
          ? 'How hiring works at Henoyo, step by step, and where you are now.'
          : `${hasArchitecture ? 'Four' : 'Three'} certifications, in order. Where you are now.`}
      </p>
      <ol className="mt-3 space-y-1">
        {stages.map((s, idx) => {
          const Icon = s.state === 'done' ? CheckCircle2 : s.state === 'now' ? CircleDot : s.state === 'locked' ? Lock : Circle;
          const tone =
            s.state === 'done' ? 'text-verdant-600' : s.state === 'now' ? 'text-terracotta-500' : 'text-ink-300 dark:text-paper-300';
          const body = (
            <div className="flex items-start gap-3 rounded-lg px-2 py-2">
              <div className="flex flex-col items-center">
                <Icon className={`h-5 w-5 ${tone}`} />
                {idx < stages.length - 1 && <span className="mt-1 h-5 w-px bg-rule dark:bg-clay-rule" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${s.state === 'locked' ? 'text-ink-400 dark:text-paper-300' : 'text-ink-900 dark:text-paper-50'}`}>
                  {s.title}
                  {s.optional && <span className="ml-1.5 text-xs font-normal text-ink-400">optional</span>}
                </p>
                <p className="text-xs text-ink-500 dark:text-paper-300">{s.text}</p>
                {s.detail && <p className="mt-0.5 text-xs font-medium text-ink-700 dark:text-paper-200">{s.detail}</p>}
              </div>
            </div>
          );
          return (
            <li key={s.title}>
              {s.href && s.state !== 'locked' ? (
                <Link to={s.href} className="block rounded-lg hover:bg-paper-50 dark:hover:bg-clay-floor">
                  {body}
                </Link>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
